// Read/extract/append helpers for the six parallel DE arrays of a
// CheckoutSelection (dedicated, dedicatedSubIds, dedicatedSubItemIds,
// dedicatedNicknames, dedicatedSubLineCounts, dedicatedKinds).

const EMPTY_ARR = [];

function toArr(v) {
  return Array.isArray(v) ? v : EMPTY_ARR;
}

/**
 * Normalize the DE slice of a selection into a single nested object.
 * Output arrays are always defined (empty when absent).
 *
 * @param {object | null | undefined} selection
 * @returns {{ envs: number[], subIds: string[], subItemIds: string[], nicknames: string[], subLineCounts: number[], kinds: string[] }}
 */
export function extractDedicatedArrays(selection) {
  if (!selection) {
    return { envs: [], subIds: [], subItemIds: [], nicknames: [], subLineCounts: [], kinds: [] };
  }
  return {
    envs: toArr(selection.dedicated),
    subIds: toArr(selection.dedicatedSubIds),
    subItemIds: toArr(selection.dedicatedSubItemIds),
    nicknames: toArr(selection.dedicatedNicknames),
    subLineCounts: toArr(selection.dedicatedSubLineCounts),
    kinds: toArr(selection.dedicatedKinds),
  };
}

/**
 * True when any DE slot is bound to an existing sub/line/nick. Used to detect
 * "ownership leakage" — DE config carried over from a signed-in selection that
 * shouldn't apply in guest/new-checkout flows.
 *
 * @param {object | null | undefined} selection
 */
export function hasOwnerBoundDedicated(selection) {
  const d = extractDedicatedArrays(selection);
  return (
    d.subIds.some(Boolean) ||
    d.subItemIds.some(Boolean) ||
    d.nicknames.some(Boolean) ||
    d.subLineCounts.some((n) => Number(n) > 0)
  );
}

/** Append one new env of `kind`, keeping all six parallel DE arrays in sync. */
export function appendDedicatedEnv(selection, kind, accounts = 1) {
  const d = extractDedicatedArrays(selection);
  return {
    dedicated: [...d.envs, Math.max(1, Number(accounts) || 1)],
    dedicatedSubIds: [...d.subIds, null],
    dedicatedSubItemIds: [...d.subItemIds, null],
    dedicatedNicknames: [...d.nicknames, null],
    dedicatedSubLineCounts: [...d.subLineCounts, 0],
    dedicatedKinds: [...d.kinds, kind]
  };
}

/** Selection patch that clears every DE field. */
export const EMPTY_DEDICATED_PATCH = Object.freeze({
  dedicated: [],
  dedicatedSubIds: [],
  dedicatedSubItemIds: [],
  dedicatedNicknames: [],
  dedicatedSubLineCounts: [],
  dedicatedKinds: [],
});
